import {
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Tabs,
  Text
} from '@mantine/core';
import React from 'react';
import { ModuleData } from './ModalRespondentResponse';
import { IconExternalLink, IconFileText, IconFolder } from '@tabler/icons-react';
import { formatLocalDate } from 'utils/convertLocalFormat';
import { RESPONSES_TYPE_COLOR } from 'constant/responseTypeColor';

interface AccordionModuleDetailProps {
  data: ModuleData;
}

const AccordionModuleDetail: React.FC<AccordionModuleDetailProps> = ({ data }) => {
  return (
    <Accordion.Item key={data.moduleID} value={data.moduleID}>
      <Accordion.Control icon={<IconFolder />}>{data.moduleName}</Accordion.Control>
      <Accordion.Panel>
        {(() => {
          // Get unique respondent types with proper typing
          const respondentTypes: string[] = Array.from(
            new Set(data.questions?.map((q: any) => q.respondentType as string) || [])
          );

          return (
            <Box p='md'>
              <Tabs defaultValue={respondentTypes[0] || 'all'}>
                <Tabs.List mb='md'>
                  {respondentTypes.map((respType: string) => (
                    <Tabs.Tab
                      key={respType}
                      value={respType}
                      leftSection={
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor:
                              RESPONSES_TYPE_COLOR[respType as keyof typeof RESPONSES_TYPE_COLOR] ||
                              'gray'
                          }}
                        />
                      }
                    >
                      {respType}
                    </Tabs.Tab>
                  ))}
                </Tabs.List>

                {respondentTypes.map((respType: string) => (
                  <Tabs.Panel key={respType} value={respType}>
                    <Stack gap={12}>
                      {data.questions
                        ?.filter((q: any) => q.respondentType === respType)
                        .map((question: any, qIndex: number) => (
                          <Card
                            key={qIndex}
                            withBorder
                            p='md'
                            radius='md'
                            style={{
                              borderLeft: `3px solid ${
                                RESPONSES_TYPE_COLOR[
                                  respType as keyof typeof RESPONSES_TYPE_COLOR
                                ] || 'gray'
                              }`
                            }}
                          >
                            <Stack gap={10} style={{ flex: 1 }}>
                              {/* Question on its own line */}
                              <Text fw={600} size='sm'>
                                {question.title}
                              </Text>

                              {/* Answer on a new line; preserve whitespace/newlines */}
                              {question.type === 'Range' ? (
                                <Group gap={8}>
                                  <Badge
                                    size='lg'
                                    style={{ minWidth: 36, textAlign: 'center' }}
                                    color={
                                      parseInt(question.answer) > 3
                                        ? 'green'
                                        : parseInt(question.answer) < 2
                                          ? 'red'
                                          : 'yellow'
                                    }
                                  >
                                    {question.answer}
                                  </Badge>
                                  {question.answerLabel && (
                                    <Text size='sm' c='dimmed'>
                                      {question.answerLabel}
                                    </Text>
                                  )}
                                </Group>
                              ) : (
                                <Box
                                  p='sm'
                                  style={{
                                    backgroundColor: 'var(--mantine-color-gray-1)',
                                    borderRadius: 8,
                                    fontSize: 14,
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
                                    {question.answer || '-'}
                                  </Text>
                                </Box>
                              )}

                              {/* Optional notes display, if present */}
                              {question.notes && (
                                <Text size='xs' c='dimmed' style={{ whiteSpace: 'pre-wrap' }}>
                                  Notes: {question.notes}
                                </Text>
                              )}
                            </Stack>
                          </Card>
                        ))}
                    </Stack>
                  </Tabs.Panel>
                ))}
              </Tabs>

              {/* Display evidence if available */}
              {data.evidences && data.evidences.length > 0 && (
                <Box mt={20}>
                  <Divider
                    my='md'
                    label={<Text fw={600}>Evidence Files</Text>}
                    labelPosition='center'
                  />
                  <SimpleGrid cols={2} spacing='md'>
                    {data.evidences.map((evidence: any, eIndex: number) => (
                      <Card key={eIndex} p='sm' withBorder radius='md' shadow='sm'>
                        <Group justify='space-between'>
                          <Group>
                            <IconFileText size={18} />
                            <Text size='sm' truncate>
                              {evidence.fileName || 'File'}
                            </Text>
                          </Group>
                          {evidence.evidenceUrl && (
                            <Button
                              size='xs'
                              variant='light'
                              color='primary'
                              component='a'
                              href={evidence.evidenceUrl}
                              target='_blank'
                              rightSection={<IconExternalLink size={14} />}
                            >
                              View
                            </Button>
                          )}
                        </Group>
                        <Group justify='space-between' mt={4}>
                          {evidence.fileSize && (
                            <Badge size='sm' variant='outline' color='gray'>
                              {evidence.fileSize}
                            </Badge>
                          )}
                          {evidence.uploadDate && (
                            <Text size='xs' c='dimmed'>
                              {formatLocalDate(evidence.uploadDate, 'MMM D, YYYY')}
                            </Text>
                          )}
                        </Group>
                      </Card>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </Box>
          );
        })()}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default AccordionModuleDetail;
